// Typed transport for /api/deal-flow/metrics/*.
// Mirrors the response shapes in PhoenixCapital.Core/DTOs/DealFlow/Metrics/.

import { api } from "../client";
import type { CriteriaLane, CriteriaTier } from "./types";

export type DealPipelineStageMetric =
  | "Intake"
  | "PreApproval"
  | "Underwriting"
  | "Closing"
  | "Closed"
  | "Declined"
  | "Withdrawn";

export type VolumePeriod = "Monthly" | "Quarterly" | "Yearly";

export type CommissionTrendDirection = "Flat" | "Up" | "Down";

export interface MetricsFilter {
  from?: string;
  to?: string;
  stage?: DealPipelineStageMetric;
  broker?: string;
  analyst?: string;
}

// --- Pipeline ----------------------------------------------------------
export interface PipelineStageBucket {
  stage: DealPipelineStageMetric;
  displayName: string;
  dealCount: number;
  totalValueUsd: number;
}
export interface StageAverageTime {
  stage: DealPipelineStageMetric;
  displayName: string;
  averageDays: number;
  sampleSize: number;
}
export interface SankeyFlow {
  fromState: string;
  toState: string;
  count: number;
}
export interface ThroughputDataPoint {
  date: string;
  advanced: number;
  declined: number;
  stalled: number;
}
export interface PipelineMetricsResponse {
  stageBuckets: PipelineStageBucket[];
  averageTimePerStage: StageAverageTime[];
  advancingDecliningStalled: SankeyFlow[];
  rollingThroughput30d: ThroughputDataPoint[];
}

// --- Volume ------------------------------------------------------------
export interface VolumeBucket {
  bucketStart: string;
  label: string;
  submitted: number;
  approved: number;
  declined: number;
  withdrawn: number;
  closed: number;
}
export interface VolumeMetricsResponse {
  period: VolumePeriod;
  buckets: VolumeBucket[];
}

// --- Quality -----------------------------------------------------------
export interface DqrDistributionBucket {
  combinedDqr: number;
  label: string;
  count: number;
}
export interface FailuresByTierBucket {
  tier: CriteriaTier | null;
  label: string;
  count: number;
}
export interface FailuresByLaneBucket {
  lane: CriteriaLane;
  label: string;
  count: number;
}
export interface TopFailureType {
  code: string;
  title: string;
  tier: CriteriaTier;
  lane: CriteriaLane;
  count: number;
}
export interface QualityMetricsResponse {
  dqrDistribution: DqrDistributionBucket[];
  failuresByTier: FailuresByTierBucket[];
  failuresByLane: FailuresByLaneBucket[];
  topFailures: TopFailureType[];
}

// --- Financial ---------------------------------------------------------
export interface FinancialStatCards {
  totalSubmittedUsd: number;
  totalClosedUsd: number;
  averageDealSizeUsd: number;
  averageDealSampleSize: number;
  commissionLast30dUsd: number;
  commissionPrior30dUsd: number;
  commissionTrend: CommissionTrendDirection;
}
export interface DealSizeBucket {
  band: string;
  lowerInclusive: number;
  upperExclusive: number | null;
  dealCount: number;
}
export interface CommissionPoint {
  monthStart: string;
  label: string;
  amountUsd: number;
}
export interface FinancialMetricsResponse {
  statCards: FinancialStatCards;
  dealSizeDistribution: DealSizeBucket[];
  commissionRolling12Months: CommissionPoint[];
}

// --- Analyst Load (DealFlow.ViewAllMetrics only) ----------------------
export interface AnalystCaseBucket {
  analystUserId: string;
  displayName: string;
  roleCode: string;
  caseCount: number;
}
export interface AnalystLaneTimeBox {
  analystUserId: string;
  displayName: string;
  lane: CriteriaLane;
  minDays: number;
  q1Days: number;
  medianDays: number;
  q3Days: number;
  maxDays: number;
  sampleSize: number;
}
export interface AnalystDecisionRatio {
  analystUserId: string;
  displayName: string;
  passes: number;
  fails: number;
}
export interface UtilizationCell {
  analystUserId: string;
  displayName: string;
  weekStart: string;
  activeCases: number;
  weightedScore: number;
}
export interface AnalystLoadMetricsResponse {
  casesPerAnalyst: AnalystCaseBucket[];
  timePerCasePerLane: AnalystLaneTimeBox[];
  decisionRatios: AnalystDecisionRatio[];
  utilizationHeatmap: UtilizationCell[];
}

// --- Time to Close -----------------------------------------------------
export interface CycleCohortPoint {
  cohortQuarter: string;
  closedCount: number;
  averageDays: number;
}
export interface CycleTimeBucket {
  band: string;
  minDaysInclusive: number;
  maxDaysExclusive: number | null;
  dealCount: number;
}
export interface CycleTimeBySizeBox {
  sizeBand: string;
  minDays: number;
  q1Days: number;
  medianDays: number;
  q3Days: number;
  maxDays: number;
  sampleSize: number;
}
export interface TimeToCloseMetricsResponse {
  cohortLines: CycleCohortPoint[];
  cycleTimeDistribution: CycleTimeBucket[];
  cycleTimeBySizeBand: CycleTimeBySizeBox[];
}

// --- API -------------------------------------------------------------
const BASE = "/deal-flow/metrics";

function filterToQuery(filter: MetricsFilter): Record<string, string> {
  const q: Record<string, string> = {};
  if (filter.from) q.from = filter.from;
  if (filter.to) q.to = filter.to;
  if (filter.stage) q.stage = filter.stage;
  if (filter.broker) q.broker = filter.broker;
  if (filter.analyst) q.analyst = filter.analyst;
  return q;
}

export const metricsApi = {
  pipeline: (filter: MetricsFilter) =>
    api.get<PipelineMetricsResponse>(`${BASE}/pipeline`, filterToQuery(filter)),
  volume: (filter: MetricsFilter, period: VolumePeriod) =>
    api.get<VolumeMetricsResponse>(`${BASE}/volume`, { ...filterToQuery(filter), period }),
  quality: (filter: MetricsFilter) =>
    api.get<QualityMetricsResponse>(`${BASE}/quality`, filterToQuery(filter)),
  financial: (filter: MetricsFilter) =>
    api.get<FinancialMetricsResponse>(`${BASE}/financial`, filterToQuery(filter)),
  analystLoad: (filter: MetricsFilter) =>
    api.get<AnalystLoadMetricsResponse>(`${BASE}/analyst-load`, filterToQuery(filter)),
  timeToClose: (filter: MetricsFilter) =>
    api.get<TimeToCloseMetricsResponse>(`${BASE}/time-to-close`, filterToQuery(filter)),
};
